import { MessageCircle, Plus } from "lucide-react";
import type { MouseEvent } from "react";

import { Price, PriceValue } from "@/components/shadcnblocks/price";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface ProductList3Product {
  id: string;
  name: string;
  image: {
    src: string;
    alt: string;
  };
  link?: string;
  description: string;
  meta?: string;
  unit?: string;
  eta?: string;
  status?: "available" | "new" | "low" | "preorder";
  qualityTags?: string[];
  price: {
    regular: number;
    sale?: number;
    currency: string;
  };
  badge?: {
    text: string;
    color?: string;
  };
}

interface ProductList3Props {
  className?: string;
  title?: string;
  subtitle?: string;
  products?: ProductList3Product[];
  variant?: "grid" | "rail" | "rows";
  actionLabel?: string;
  consultLabel?: string;
  onAddToCart?: (product: ProductList3Product) => void;
  onProductSelect?: (product: ProductList3Product) => void;
}

const fallbackProducts: ProductList3Product[] = [
  {
    id: "registry-demo",
    name: "Vexon CoreStep '08 LX",
    image: {
      src: "https://deifkwefumgah.cloudfront.net/shadcnblocks/block/ecommerce/clothes/joshua-diaz-ETNoDLl8yFE-unsplash-1.jpg",
      alt: "",
    },
    link: "#",
    description:
      "Everyday comfort meets bold tri-color style in this performance-driven design.",
    price: {
      regular: 499,
      sale: 399,
      currency: "USD",
    },
    badge: {
      text: "Selling fast!",
      color: "oklch(57.7% 0.245 27.325)",
    },
  },
];

const ProductList3 = ({
  className,
  title,
  subtitle,
  products = fallbackProducts,
  variant = "grid",
  actionLabel = "Thêm vào giỏ",
  consultLabel = "Tư vấn đặt hàng",
  onAddToCart,
  onProductSelect,
}: ProductList3Props) => {
  return (
    <section
      className={cn(
        "shadcn-product-list3",
        `shadcn-product-list3--${variant}`,
        className,
      )}
    >
      {(title || subtitle) && (
        <div className="shadcn-product-list3__heading">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <p>{subtitle}</p>}
          </div>
        </div>
      )}
      <div className="shadcn-product-list3__items">
        {products.map((item) => (
          <ProductCard
            key={item.id}
            product={item}
            variant={variant}
            actionLabel={actionLabel}
            consultLabel={consultLabel}
            onAddToCart={onAddToCart}
            onProductSelect={onProductSelect}
          />
        ))}
      </div>
    </section>
  );
};

const ProductCard = ({
  product,
  variant,
  actionLabel,
  consultLabel,
  onAddToCart,
  onProductSelect,
}: {
  product: ProductList3Product;
  variant: "grid" | "rail" | "rows";
  actionLabel: string;
  consultLabel: string;
  onAddToCart?: (product: ProductList3Product) => void;
  onProductSelect?: (product: ProductList3Product) => void;
}) => {
  const { name, description, image, badge, price, meta, eta, qualityTags, unit } =
    product;
  const isPreorder = product.status === "preorder";
  const buttonLabel = isPreorder ? consultLabel : actionLabel;
  const ButtonIcon = isPreorder ? MessageCircle : Plus;

  const handleSelect = () => {
    onProductSelect?.(product);
  };

  const handleAction = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onAddToCart?.(product);
  };

  return (
    <Card
      className={cn(
        "shadcn-product-card group overflow-hidden p-0",
        variant === "rows" && "shadcn-product-card--row",
      )}
      onClick={handleSelect}
    >
      <CardHeader className="shadcn-product-card__media relative block p-0">
        <AspectRatio ratio={variant === "rows" ? 1 : 1.05} className="overflow-hidden">
          <img
            src={image.src}
            alt={image.alt}
            className="block size-full object-cover object-center"
          />
        </AspectRatio>
        <CardAction className="shadcn-product-card__action">
          <Button
            className={cn(
              "w-full",
              isPreorder && "bg-blue-600 text-white hover:bg-blue-700",
            )}
            type="button"
            onClick={handleAction}
          >
            <ButtonIcon className="size-4" />
            {buttonLabel}
          </Button>
        </CardAction>
        {badge && (
          <Badge
            style={{
              backgroundColor: badge.color,
            }}
            className="absolute start-3 top-3"
          >
            {badge.text}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="shadcn-product-card__content">
        <CardTitle className="shadcn-product-card__title">{name}</CardTitle>
        <CardDescription className="shadcn-product-card__description">
          {description}
        </CardDescription>
        {meta && <p className="shadcn-product-card__meta">{meta}</p>}
        {qualityTags && qualityTags.length > 0 && (
          <div className="shadcn-product-card__tags">
            {qualityTags.slice(0, 3).map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        )}
        {eta ? (
          <div className="shadcn-product-card__eta">
            <span>Ngày hàng về dự kiến:</span>
            <strong>{eta}</strong>
          </div>
        ) : (
          <div className="shadcn-product-card__price">
            <Price onSale={price.sale != null}>
              <PriceValue
                price={price.sale}
                currency={price.currency}
                locale="vi-VN"
                variant="sale"
                className="text-red-600"
              />
              <PriceValue
                price={price.regular}
                currency={price.currency}
                locale="vi-VN"
                variant="regular"
              />
            </Price>
            {unit && <span>{unit}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export { ProductList3 };
